import { useSearchParams } from "react-router";

import { Segmented } from "@/components/segmented";
import type { RangeDays } from "@/lib/types";

export const RANGE_OPTIONS: readonly { value: RangeDays; label: string }[] = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "12 months" },
];

const DEFAULT_DAYS: RangeDays = 30;

/** `?days=` as a range the API accepts; anything else is the 30-day default. */
export function parseDays(raw: string | null): RangeDays {
  const n = Number(raw);
  return RANGE_OPTIONS.some((o) => o.value === n)
    ? (n as RangeDays)
    : DEFAULT_DAYS;
}

/** The range in the URL (`?days=`), and a setter that keeps the other params. */
export function useRangeDays(): [RangeDays, (days: RangeDays) => void] {
  const [params, setParams] = useSearchParams();
  const days = parseDays(params.get("days"));
  const setDays = (next: RangeDays) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === DEFAULT_DAYS) out.delete("days");
        else out.set("days", String(next));
        return out;
      },
      { replace: true },
    );
  return [days, setDays];
}

/** The 7 / 30 / 90 days / 12 months switch. Pair it with `useRangeDays`. */
export function RangePicker({
  value,
  onChange,
  size = "sm",
}: {
  value: RangeDays;
  onChange: (days: RangeDays) => void;
  size?: "sm" | "default";
}) {
  return (
    <Segmented
      aria-label="Date range"
      size={size}
      value={String(value)}
      onChange={(v) => onChange(parseDays(v))}
      options={RANGE_OPTIONS.map((o) => ({
        value: String(o.value),
        label: o.label,
      }))}
    />
  );
}
